import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { GetIntellipayWidget, PostPayment } from "../services/PaymentService";
import { useAxiosInstance } from "../services/Interceptor";
import { RegisterAthlete } from "../services/EventsService";
import { useSelector } from "react-redux";
import { registerCoach } from "../services/CoachService";
import {
  registerAthlete,
  registerAthleteWithEventId,
} from "../services/AthleteService";
import {
  GetAdminPrice,
  GetEventCombinePrice,
  SaveAppliedCouponCode,
} from "../services/EventsService";
import { useTrackEvent } from "../hooks/useTrackEvent";

export default function PayWidgetEvents(props) {
  const { onIntellipayReady } = props;
  const apiInstance = useAxiosInstance();
  const accounts = useSelector((state) => state.account.register);
  const { subscriptionType, subscriptionPrice } = useSelector(
    (state) => state.account,
  );
  const navigate = useNavigate();

  const trackEvent = useTrackEvent();
  const trackEventRef = useRef(trackEvent);

  const subscriptionTypeRef = useRef(subscriptionType);
  const subscriptionPriceRef = useRef(subscriptionPrice);
  const intervalRef = useRef(null);
  const isReInitializationRef = useRef(false);

  useEffect(() => {
    trackEventRef.current = trackEvent;
  }, [trackEvent]);

  useEffect(() => {
    subscriptionTypeRef.current = subscriptionType;
    subscriptionPriceRef.current = subscriptionPrice;
  }, [subscriptionType, subscriptionPrice]);

  useEffect(() => {
    // Track if this is a re-initialization (widget already exists)
    isReInitializationRef.current = !!(
      window.intellipay && window.__intellipayWidgetLoaded
    );

    getIntellipayWidget();

    // Cleanup function to clear intervals only
    // Don't reset the widget flag since the script persists across route changes
    return () => {
      const currentInterval = intervalRef.current;
      if (currentInterval) {
        clearInterval(currentInterval);
      }
      // Don't reset window.__intellipayWidgetLoaded here
      // The script stays in the DOM during React Router navigation
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetIntellipayWidget = () => {
    // Only reset after non-approval, not on unmount
    window.__intellipayWidgetLoaded = false;
  };

  const handleFixAmount = (inputValue) => {
    if (typeof inputValue !== "string") {
      if (typeof inputValue === "number") {
        return inputValue.toFixed(2);
      } else {
        console.warn("Invalid amount format. Returning default value.");
        return "0.00";
      }
    }

    if (
      window.intellipay &&
      typeof window.intellipay.fixAmount === "function"
    ) {
      let tempamount = window.intellipay.fixAmount(inputValue);
      return tempamount;
    } else {
      console.warn("fixAmount is missing! Using fallback.");
      return parseFloat(inputValue.replace(/[^0-9.]/g, "") || "0").toFixed(2);
    }
  };
  const accountsRef = useRef(accounts);

  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

  const getAthletePricing = async () => {
    const res = await GetAdminPrice(apiInstance);
    const athlete = res?.data?.find(
      (item) => item.role.toLowerCase() === "athlete",
    );
    return athlete?.pricingOptions || {};
  };

  const getCombinePositionDrillPrice = async (response, props) => {
    const combinePriceRes = await GetEventCombinePrice(
      apiInstance,
      props.eventId,
    );
    let combineDrillPayload = [];

    if (props.subscriptionType === "combinePosition") {
      combineDrillPayload.push({
        userId: props.userId,
        amount: handleFixAmount(combinePriceRes?.data?.combinePrice),
        isCombine: true,
        isPositionDrill: false,
        isAnnualSubscription: false,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.transactionId || "N/A",
        intellipayToken: String(response?.custid || ""),
      });

      combineDrillPayload.push({
        userId: props.userId,
        amount: handleFixAmount(combinePriceRes?.data?.positionDrillPrice),
        isCombine: false,
        isPositionDrill: true,
        isAnnualSubscription: false,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.transactionId || "N/A",
        intellipayToken: String(response?.custid || ""),
      });
    } else {
      combineDrillPayload.push({
        userId: props.userId,
        amount: handleFixAmount(combinePriceRes?.data?.combinePrice),
        isCombine: true,
        isPositionDrill: false,
        isAnnualSubscription: false,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.transactionId || "N/A",
        intellipayToken: String(response?.custid || ""),
      });
    }

    return combineDrillPayload;
  };

  const postAppliedCouponCode = async (code) => {
    await SaveAppliedCouponCode(apiInstance, code);
    localStorage.removeItem("appliedCouponCode");
  };

  const postPaymentSuccess = async (response, props) => {
    let combineDrillPayload = [];
    if (
      props.subscriptionType === "combine" ||
      props.subscriptionType === "combinePosition"
    ) {
      combineDrillPayload = await getCombinePositionDrillPrice(response, props);
    }

    const currentSubscriptionType = subscriptionTypeRef.current;

    const isCombine = Array.isArray(currentSubscriptionType)
      ? currentSubscriptionType.includes("combine")
      : currentSubscriptionType === "combine";

    const isMonthlySubscription = Array.isArray(currentSubscriptionType)
      ? currentSubscriptionType.includes("monthly")
      : currentSubscriptionType === "monthly";

    const isAnnualSubscription = Array.isArray(currentSubscriptionType)
      ? currentSubscriptionType.includes("annual")
      : currentSubscriptionType === "annual";

    const isCombinePositionDrill = Array.isArray(currentSubscriptionType)
      ? currentSubscriptionType.includes("positionDrill")
      : currentSubscriptionType === "positionDrill";

    const isSubscription = Array.isArray(currentSubscriptionType)
      ? currentSubscriptionType.includes("subscription")
      : currentSubscriptionType === "subscription";

    let combinePrice = 0;
    let positionDrillPrice = 0;
    let athletePrices = {};
    let paymentPayloads = [];

    const combinePriceResponse = await GetEventCombinePrice(
      apiInstance,
      props.eventId,
    );

    if (isCombine) {
      combinePrice = combinePriceResponse?.data?.combinePrice || 0;

      paymentPayloads.push({
        userId: props.userId,
        amount: handleFixAmount(combinePrice),
        isCombine: true,
        isPositionDrill: false,
        isAnnualSubscription: false,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.transactionId || "N/A",
        intellipayToken: String(response?.custid || ""),
      });
    }

    positionDrillPrice = combinePriceResponse?.data?.positionDrillPrice || 0;
    athletePrices = await getAthletePricing();

    if (isCombinePositionDrill) {
      paymentPayloads.push({
        userId: props.userId,
        amount: handleFixAmount(positionDrillPrice),
        isCombine: false,
        isPositionDrill: true,
        isAnnualSubscription: false,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.transactionId || "N/A",
        intellipayToken: String(response?.custid || ""),
      });
    }

    if (isSubscription) {
      paymentPayloads.push({
        userId: props.userId,
        amount: handleFixAmount(athletePrices.annualPrice ?? 0),
        isCombine: false,
        isPositionDrill: false,
        isAnnualSubscription: true,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.transactionId || "N/A",
        intellipayToken: String(response?.custid || ""),
      });
    }

    if (isAnnualSubscription || isMonthlySubscription) {
      paymentPayloads.push({
        userId: props.userId,
        amount: handleFixAmount(subscriptionPriceRef.current),
        isCombine: false,
        isPositionDrill: false,
        isAnnualSubscription,
        isMonthlySubscription,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.transactionId || "N/A",
        intellipayToken: String(response?.custid || ""),
      });
    }

    try {
      const currentAccounts = accountsRef.current;
      if (currentAccounts.length > 0) {
        const user = currentAccounts[0];
        if (user.registered) {
          for (const payload of paymentPayloads) {
            const storedData = JSON.parse(
              localStorage.getItem("registeredAccount") || "{}",
            );
            const userReg = user.length ? user[0] : storedData;
            const updatedPaymentPayload = {
              ...payload,
              userId: userReg.userId,
              eventId: 0,
            };
            const paid = await PostPayment(apiInstance, updatedPaymentPayload);
            if (paid.status === 200) {
              const couponCode = localStorage.getItem("appliedCouponCode");
              if (couponCode) {
                await postAppliedCouponCode(couponCode);
              }
              toast.success(
                "Athlete payment successful.Verify Your Email to Get Started!",
                {
                  position: "top-right",
                },
              );
              setTimeout(() => {
                window.location.href = "/athlete/dashboard";
              }, 500);
            } else {
              toast.error("Your Payment is failed.", { position: "top-right" });
            }
          }
        } else if (user.role === "university") {
          const reg = await registerCoach(apiInstance, user);
          if (reg.status === 200) {
            const newUserId = reg.data.id;
            for (const payload of paymentPayloads) {
              const updatedPaymentPayload = {
                ...payload,
                userId: newUserId,
                eventId: 0,
              };

              const paid = await PostPayment(
                apiInstance,
                updatedPaymentPayload,
              );
              if (paid.status === 200) {
                toast.success("Coach registration & payment successful!", {
                  position: "top-right",
                });
                const couponCode = localStorage.getItem("appliedCouponCode");
                if (couponCode) {
                  await postAppliedCouponCode(couponCode);
                }
                setTimeout(() => {
                  navigate("/register/registrationsuccess");
                }, 500);
              } else {
                toast.error("Payment after coach registration failed.", {
                  position: "top-right",
                });
              }
            }
          } else {
            toast.error("Coach registration failed.", {
              position: "top-right",
            });
          }
        } else if (user.role === "athlete") {
          const reg = await registerAthlete(apiInstance, "0", user);
          if (reg.status === 200) {
            const newUserId = reg.data.id;
            for (const payload of paymentPayloads) {
              const updatedPaymentPayload = {
                ...payload,
                userId: newUserId,
                eventId: 0,
              };
              const paid = await PostPayment(
                apiInstance,
                updatedPaymentPayload,
              );
              if (paid.status === 200) {
                toast.success("Athlete registration & payment successful!", {
                  position: "top-right",
                });
                const couponCode = localStorage.getItem("appliedCouponCode");
                if (couponCode) {
                  await postAppliedCouponCode(couponCode);
                }
                setTimeout(() => {
                  navigate("/register/registrationsuccess");
                }, 500);
              } else {
                toast.error("Payment after athlete registration failed.", {
                  position: "top-right",
                });
              }
            }
          } else {
            toast.error("Athlete registration failed.", {
              position: "top-right",
            });
          }
        } else if (user.role === "spotcombine") {
          const spotcombineRegRes = await registerAthleteWithEventId(
            apiInstance,
            user,
            props.eventId,
          );
          if (spotcombineRegRes.status === 200) {
            const newUserId = spotcombineRegRes.data.id;
            let allSuccessful = true;

            for (const payload of paymentPayloads) {
              const updatedPaymentPayload = {
                ...payload,
                userId: newUserId,
                eventId: props.eventId,
              };

              const paid = await PostPayment(
                apiInstance,
                updatedPaymentPayload,
              );

              if (paid.status !== 200) {
                allSuccessful = false;
                toast.error("Payment after spot registration failed.", {
                  position: "top-right",
                });
                break;
              }
            }

            if (allSuccessful) {
              const couponCode = localStorage.getItem("appliedCouponCode");
              if (couponCode) {
                await postAppliedCouponCode(couponCode);
              }
              trackEventRef.current("Registration5_RegistrationCompleted", {
                event_id: props.eventId,
                role: "spotcombine",
              });
              toast.success("Athlete spot registration & payment successful!", {
                position: "top-right",
              });
              setTimeout(() => {
                navigate("/register/registrationsuccess");
              }, 500);
            }
          } else {
            toast.error("Athlete spot registration failed.", {
              position: "top-right",
            });
          }
        } else {
          const regRes = await RegisterAthlete(
            apiInstance,
            user.eventId,
            user.userId,
          );
          if (regRes.status === 200) {
            for (const payload of paymentPayloads) {
              const paidRes = await PostPayment(apiInstance, payload);
              if (paidRes.status === 200) {
                const couponCode = localStorage.getItem("appliedCouponCode");
                if (couponCode) {
                  await postAppliedCouponCode(couponCode);
                }
                toast.success(
                  "Athlete event registration and payment is successful",
                  {
                    position: "top-right",
                  },
                );
                props.onRegistrationSuccess();
              } else {
                toast.error("Payment after event registration failed.", {
                  position: "top-right",
                });
              }
            }
          } else {
            toast.error("Your event registration failed", {
              position: "top-right",
            });
          }
        }
      } else {
        const regRes = await RegisterAthlete(
          apiInstance,
          props.eventId,
          props.userId,
        );
        if (regRes.status === 200) {
          let allSuccessful = true;

          for (const combineDrills of combineDrillPayload) {
            const paidRes = await PostPayment(apiInstance, combineDrills);
            if (paidRes.status !== 200) {
              allSuccessful = false;
              toast.error("Payment after event registration failed.", {
                position: "top-right",
              });
              break;
            }
          }

          if (allSuccessful) {
            const couponCode = localStorage.getItem("appliedCouponCode");
            if (couponCode) {
              await postAppliedCouponCode(couponCode);
            }
            toast.success(
              "Athlete event combine registration and payment is successful",
              {
                position: "top-right",
              },
            );
            props.onRegistrationSuccess();
          }
        } else {
          toast.error("Your event registration failed", {
            position: "top-right",
          });
        }
      }
    } catch (error) {
      trackEventRef.current("Registration4_5_PaymentFailed", {
        event_id: props.eventId,
        reason: error.message ?? "unknown",
      });
      toast.error("Payment processing failed - " + error.error, {
        position: "top-right",
      });
    }
  };

  const getIntellipayWidget = async () => {
    // Check if window.intellipay exists (script loaded)
    if (window.intellipay) {
      if (typeof window.intellipay.disable === "function") {
        window.intellipay.disable("account");
      }
      // Re-register the callbacks first, then initialize
      window.intellipay.runOnApproval((response) => {
        postPaymentSuccess(response, props);
      });
      window.intellipay.runOnNonApproval((res) => {
        console.error("Transaction not approved!", res);
        resetIntellipayWidget();
      });

      window.intellipay.runOnClose(() => {
        // Don't trigger onClose callback when user simply closes the widget
        // The callback should only be triggered after successful payment
      });

      // Initialize AFTER setting up callbacks
      try {
        window.intellipay.initialize();

        // Wait for DOM to be ready, then update fields
        setTimeout(() => {
          // IMPORTANT: Set the values first before calling updatefields
          const amountField = document.querySelector(
            '.ipayfield[data-ipayname="amount"]',
          );
          const emailField = document.querySelector(
            '.ipayfield[data-ipayname="email"]',
          );
          const accountField = document.querySelector(
            '.ipayfield[data-ipayname="account"]',
          );

          console.log("Re-initialization: Setting field values", {
            amount: props.paymentAmount,
            email: props.userEmail,
          });

          if (amountField) {
            amountField.value = props.paymentAmount || 0;
          }
          if (emailField) {
            emailField.value = props.userEmail || "";
          }
          if (accountField) {
            accountField.value = "0";
          }

          // Use setvalue if available
          if (typeof window.intellipay.setvalue === "function") {
            window.intellipay.setvalue("account", "0");
            window.intellipay.setvalue(
              "amount",
              String(props.paymentAmount || 0),
            );
            window.intellipay.setvalue("email", props.userEmail || "");
          }

          // Now call updatefields to sync
          if (typeof window.intellipay.updatefields === "function") {
            window.intellipay.updatefields();
          }

          // Also try calling readyHandler to ensure it's fully ready
          if (typeof window.intellipay.readyHandler === "function") {
            window.intellipay.readyHandler();
          }
        }, 100);
      } catch (e) {
        console.warn("Error during initialize:", e);
      }

      window.__intellipayWidgetLoaded = true;
      onIntellipayReady?.(true);
      return;
    }

    // Check if script already exists in DOM but not fully loaded yet
    const existingScript = document.querySelector("script[src*='intellipay']");
    const existingContainer =
      document.querySelector("#intellipay-container") ||
      document.querySelector("[id*='intellipay']");

    if (existingScript || existingContainer) {
      // Set a timeout to prevent infinite waiting
      let attempts = 0;
      const maxAttempts = 20; // 10 seconds max

      intervalRef.current = setInterval(() => {
        attempts++;

        if (window.intellipay) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;

          if (typeof window.intellipay.disable === "function") {
            window.intellipay.disable("account");
          }

          window.intellipay.initialize();
          window.intellipay.runOnApproval((response) => {
            postPaymentSuccess(response, props);
          });
          window.intellipay.runOnNonApproval((res) => {
            console.error("Transaction not approved!", res);
            resetIntellipayWidget();
          });

          window.intellipay.runOnClose(() => {
            // Don't trigger onClose callback when user simply closes the widget
            // The callback should only be triggered after successful payment
          });

          window.__intellipayWidgetLoaded = true;
          trackEventRef.current("Registration4_OpenedPaymentModal", {
            event_id: props.eventId,
          });
          onIntellipayReady?.(true);
        } else if (attempts >= maxAttempts) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          console.error(
            "Intellipay widget failed to load after maximum attempts",
          );
        }
      }, 500);
      return;
    }

    // Load the widget for the first time
    const response = await GetIntellipayWidget(apiInstance);
    if (response.success) {
      const range = document.createRange();
      const fragment = range.createContextualFragment(response.data);

      // Only append if not already in the DOM
      if (!document.querySelector("script[src*='intellipay']")) {
        document.documentElement.appendChild(fragment);
      }

      let attempts = 0;
      const maxAttempts = 20; // 10 seconds max

      intervalRef.current = setInterval(() => {
        attempts++;

        if (window.intellipay) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;

          if (typeof window.intellipay.disable === "function") {
            window.intellipay.disable("account");
          }

          window.intellipay.initialize();
          window.intellipay.runOnApproval((response) => {
            postPaymentSuccess(response, props);
          });
          window.intellipay.runOnNonApproval((res) => {
            console.error("Transaction not approved!", res);
            resetIntellipayWidget();
          });

          window.intellipay.runOnClose(() => {
            // Don't trigger onClose callback when user simply closes the widget
            // The callback should only be triggered after successful payment
          });

          window.__intellipayWidgetLoaded = true;
          trackEventRef.current("Registration4_OpenedPaymentModal", {
            event_id: props.eventId,
          });
          onIntellipayReady?.(true);
        } else if (attempts >= maxAttempts) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          console.error(
            "Intellipay widget failed to load after maximum attempts",
          );
        }
      }, 500);
    }
  };

  useEffect(() => {
    // Update intellipay fields when props change
    if (window.intellipay && props.paymentAmount !== undefined) {
      console.log(
        "Props changed - paymentAmount:",
        props.paymentAmount,
        "userEmail:",
        props.userEmail,
      );

      const updateIntellipayFields = () => {
        console.log(
          "Attempting to update Intellipay fields with amount:",
          props.paymentAmount,
        );

        // Update the hidden input fields directly
        const amountField = document.querySelector(
          '.ipayfield[data-ipayname="amount"]',
        );
        const emailField = document.querySelector(
          '.ipayfield[data-ipayname="email"]',
        );
        const accountField = document.querySelector(
          '.ipayfield[data-ipayname="account"]',
        );

        console.log("Found fields:", {
          amountField: amountField?.value,
          emailField: emailField?.value,
          accountField: accountField?.value,
        });

        if (amountField) {
          amountField.value = props.paymentAmount || 0;
          console.log("Set amountField.value to:", amountField.value);
        }
        if (emailField) {
          emailField.value = props.userEmail || "";
          console.log("Set emailField.value to:", emailField.value);
        }
        if (accountField) {
          accountField.value = "0";
        }

        // Call updatefields to sync with Intellipay widget
        if (window.intellipay) {
          if (typeof window.intellipay.disable === "function") {
            window.intellipay.disable("account");
          }
          if (typeof window.intellipay.updatefields === "function") {
            console.log("Calling window.intellipay.updatefields()");
            window.intellipay.updatefields();
          }

          // Also use setvalue if available
          if (typeof window.intellipay.setvalue === "function") {
            console.log("Calling window.intellipay.setvalue()");
            window.intellipay.setvalue("account", "0");
            window.intellipay.setvalue(
              "amount",
              String(props.paymentAmount || 0),
            );
            window.intellipay.setvalue("email", props.userEmail || "");
          }
        }
      };

      // Use a small delay to ensure DOM is ready
      setTimeout(() => {
        updateIntellipayFields();
      }, 50);
    }
  }, [props.paymentAmount, props.userEmail]);

  useEffect(() => {
    // Manually handle submit button clicks ONLY when widget is re-initialized
    const handleSubmitClick = (e) => {
      if (e.target.dataset.ipayname === "submit") {
        // ONLY manually intervene if this was a re-initialization
        if (isReInitializationRef.current && window.intellipay) {
          e.preventDefault();
          e.stopPropagation();

          if (typeof window.intellipay.disable === "function") {
            window.intellipay.disable("account");
          }

          // Update fields right before opening to ensure current values are used
          if (typeof window.intellipay.updatefields === "function") {
            window.intellipay.updatefields();
          }

          // Set values explicitly using setvalue if available
          const amountField = document.querySelector(
            '.ipayfield[data-ipayname="amount"]',
          );
          const emailField = document.querySelector(
            '.ipayfield[data-ipayname="email"]',
          );
          const accountField = document.querySelector(
            '.ipayfield[data-ipayname="account"]',
          );

          if (typeof window.intellipay.setvalue === "function") {
            if (accountField) {
              window.intellipay.setvalue("account", accountField.value);
            }
            if (amountField) {
              window.intellipay.setvalue("amount", amountField.value);
            }
            if (emailField) {
              window.intellipay.setvalue("email", emailField.value);
            }
          }

          // Try to trigger the form submission properly
          // First try onSubmitInternal which should process the form values
          if (typeof window.intellipay.onSubmitInternal === "function") {
            window.intellipay.onSubmitInternal(e);
          } else if (typeof window.intellipay.onSubmit === "function") {
            window.intellipay.onSubmit(e);
          } else if (typeof window.intellipay.checkform === "function") {
            const formValid = window.intellipay.checkform();
            if (
              formValid &&
              typeof window.intellipay.openModal === "function"
            ) {
              window.intellipay.openModal();
            }
          } else if (typeof window.intellipay.openModal === "function") {
            window.intellipay.openModal();
          }
        }
      }
    };

    // Use capture phase to intercept before Intellipay's listener
    document.addEventListener("click", handleSubmitClick, true);
    return () => document.removeEventListener("click", handleSubmitClick, true);
  }, []);

  return (
    <div>
      <input
        className="ipayfield"
        data-ipayname="account"
        type="hidden"
        value={0}
      />
      <input
        className="ipayfield"
        data-ipayname="email"
        type="hidden"
        value={props.userEmail || ""}
      />
      <input
        className="ipayfield"
        data-ipayname="amount"
        type="hidden"
        value={props.paymentAmount || 0}
      />
    </div>
  );
}
